import {Worlds} from "@zwiftalizer/parser/lib/esm/types/worlds";

const getWorldName = (id: string) => Worlds.get(parseInt(id)) ?? "Unknown";

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.substr(1).toLowerCase();

const localizeDate = (timestamp: number, timezoneOffsetMinutes: number): Date => {
  if (!timestamp || isNaN(timestamp)) {
    throw new Error("Invalid timestamp");
  }
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date");
  }
  return new Date(date.getTime() + (timezoneOffsetMinutes || 0) * 60 * 1000);
};

const formatDate = (timestamp: number, timezoneOffsetMinutes: number): string => {
  if (!timestamp || isNaN(timestamp)) {
    return "N/A";
  }
  try {
    return localizeDate(timestamp, timezoneOffsetMinutes).toISOString().slice(0, 10);
  } catch (e) {
    return "Invalid Date";
  }
};

const formatTime = (timestamp: number, timezoneOffsetMinutes: number): string => {
  if (!timestamp || isNaN(timestamp)) {
    return "N/A";
  }
  try {
    return localizeDate(timestamp, timezoneOffsetMinutes).toISOString().slice(-13, -5);
  } catch (e) {
    return "Invalid Time";
  }
};

const formatIso8601DateTime = (datetime: string, timezoneOffsetMinutes: number): string => {
  if (!datetime) {
    return "N/A";
  }
  const timestamp: number = Date.parse(datetime);
  if (isNaN(timestamp)) {
    return "Invalid Date";
  }
  try {
    return localizeDate(timestamp, timezoneOffsetMinutes).toISOString().slice(0, 10);
  } catch (e) {
    return "Invalid Date";
  }
};

const humanizeDuration = (d: number): string => {
  const hours: number = Math.floor(d / 3600);
  const mins: number = Math.floor((d % 3600) / 60);
  const seconds: number = Math.floor((d % 3600) % 60);

  let humanizedDuration: string = "";

  if (hours > 0) {
    humanizedDuration = `${hours} hr${hours === 0 || hours > 1 ? "s" : ""} ${mins} min${
      mins === 0 || mins > 1 ? "s" : ""
    }`;
  } else if (mins > 0) {
    humanizedDuration = `${mins} min${mins === 0 || mins > 1 ? "s" : ""} ${seconds} sec${
      seconds === 0 || seconds > 1 ? "s" : ""
    }`;
  } else if (seconds > 0) {
    humanizedDuration = `${seconds} sec${seconds === 0 || seconds > 1 ? "s" : ""}`;
  }

  return humanizedDuration;
};

function convertGroupEventDateFormat(dateStr: string | undefined): string {
  if (dateStr === undefined) {
    return "";
  }
  // Split the date and the combined time-am/pm parts
  const [datePart, timePart, amPmPart] = dateStr.split(" ");

  // Split the date into month, day, and year
  const [month, day, year] = datePart.split("/");

  // Extract hours, minutes and AM/PM from the time part
  const [hoursStr, minutesStr] = timePart.split(":");

  // Convert the time to 24-hour format
  let hours = parseInt(hoursStr, 10);

  if (amPmPart === "PM" && hours < 12) {
    hours += 12;
  } else if (amPmPart === "AM" && hours === 12) {
    hours = 0;
  }

  // Pad the hours and minutes with leading zeros if necessary
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutesStr.padStart(2, "0");

  // Reformat the date into YYYY-MM-DD
  const reformattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;

  // Combine the reformatted date with the reformatted time
  return `${reformattedDate} ${formattedHours}:${formattedMinutes}`;
}

/**
 * Converts UTC timestamp to localized timestamp by applying timezone offset
 * @param utcTimestamp - Unix timestamp in milliseconds (UTC)
 * @param timezoneOffsetMinutes - Timezone offset in minutes
 * @returns Localized timestamp in milliseconds
 */
const localizeTimestamp = (utcTimestamp: number, timezoneOffsetMinutes: number): number => {
  return utcTimestamp + (timezoneOffsetMinutes || 0) * 60 * 1000;
};

/**
 * Localizes an array of data points where first element is a timestamp
 * @param points - Array of data points where first element is UTC timestamp
 * @param timezoneOffsetMinutes - Timezone offset in minutes
 * @returns Array with localized timestamps
 */
const localizeDataPoints = <T extends [number, ...any[]]>(points: T[], timezoneOffsetMinutes: number): T[] => {
  if (!points || !Array.isArray(points)) {
    return points;
  }
  return points.map((point) => {
    if (!Array.isArray(point) || point.length === 0) {
      return point;
    }
    return [localizeTimestamp(point[0], timezoneOffsetMinutes), ...point.slice(1)] as T;
  });
};

export {
  titleCase,
  getWorldName,
  localizeDate,
  localizeTimestamp,
  localizeDataPoints,
  formatDate,
  formatTime,
  formatIso8601DateTime,
  humanizeDuration,
  convertGroupEventDateFormat
};
